/* AP PDP v5 motion (masi-claude for Codex). Load after pdp.js. No dependencies.
   1. Offer cards: the selected card's perks open and the previous card's perks close with a height tween, so the
      cards below and the CTA glide instead of jumping. It measures before pdp.js toggles .selected (capture phase)
      and animates after (next frame), so radio selection and price updates are never blocked or delayed.
   2. Bag drawer (#bag-dialog): short slide-and-fade in and out, including Escape and backdrop clicks.
   3. Accordions (<details> inside <main>): height tween on open and close; keyboard and native semantics kept.
   4. #renewal-note: a brief fade when its text changes, so the new renewal terms are noticed.
   Everything is skipped under prefers-reduced-motion or body.paused (the page's Pause motion control), and every
   animation can be finished early by pdp.js applyMotion() without leaving a half state. */
(() => {
  'use strict';
  if (window.apPdpMotion) return;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const still = () => reduce.matches || document.body.classList.contains('paused');
  const EASE = 'cubic-bezier(.22,1,.36,1)'; // same curve as animatePdp
  const EASE_OUT = 'cubic-bezier(.4,0,.2,1)';
  const OFFER_MS = 320;
  const px = n => `${Math.max(0, n)}px`;
  const shown = el => !!el && getComputedStyle(el).display !== 'none';
  const settle = (anim, done) => { if (!anim) return done(); anim.addEventListener('finish', done, {once: true}); anim.addEventListener('cancel', done, {once: true}); };

  /* 1. Offer cards ------------------------------------------------------------------------------------------- */
  const perkAnims = new WeakMap();
  let pending = null;
  const offerCards = () => [...document.querySelectorAll('.offer-stack .offer-option')];

  // Capture phase: runs before pdp.js updates classes, so the old layout (or the mid-animation state) is measured.
  document.addEventListener('change', e => {
    if (!e.target.matches || !e.target.matches('input[name="offer"]')) return;
    pending = null;
    if (still()) return;
    pending = offerCards().map(card => {
      const perks = card.querySelector('.offer-perks');
      if (!perks) return {card};
      const cs = getComputedStyle(perks);
      const visible = cs.display !== 'none';
      const state = visible
        ? {h: perks.getBoundingClientRect().height, pt: cs.paddingTop, pb: cs.paddingBottom, o: cs.opacity}
        : {h: 0, pt: '0px', pb: '0px', o: '0'};
      perkAnims.get(perks)?.cancel();
      card.classList.remove('apm-closing');
      perks.classList.remove('apm-tween');
      return {card, perks, visible, state};
    });
  }, true);

  // Bubble phase: pdp.js has handled the change. Animate on the next frame (before it is painted), which also
  // covers a class update that pdp.js makes in its own requestAnimationFrame.
  document.addEventListener('change', e => {
    if (!e.target.matches || !e.target.matches('input[name="offer"]') || !pending) return;
    const before = pending; pending = null;
    requestAnimationFrame(() => {
      if (still()) return;
      before.forEach(({card, perks, visible, state}) => {
        if (!perks) return;
        const nowVisible = shown(perks);
        if (!visible && !nowVisible) return;
        if (nowVisible) {
          // Opening, or re-opening mid-close: tween from the measured state to the natural size.
          const cs = getComputedStyle(perks);
          const target = {h: perks.getBoundingClientRect().height, pt: cs.paddingTop, pb: cs.paddingBottom};
          if (visible && Math.abs(target.h - state.h) < 1) return;
          perks.classList.add('apm-tween');
          const anim = perks.animate([
            {height: px(state.h), paddingTop: state.pt, paddingBottom: state.pb, opacity: state.o},
            {height: px(target.h), paddingTop: target.pt, paddingBottom: target.pb, opacity: 1}
          ], {duration: OFFER_MS, easing: EASE});
          perkAnims.set(perks, anim);
          settle(anim, () => { if (perkAnims.get(perks) === anim) { perks.classList.remove('apm-tween'); perkAnims.delete(perks); } });
        } else {
          // Closing: keep the perks displayed (clipped) while they shrink and fade, then hand back to the CSS.
          card.classList.add('apm-closing');
          perks.classList.add('apm-tween');
          // Same duration and curve as the opening tween, so the two heights stay complementary and the
          // stack (and the CTA under it) moves in one direction only. The text fades out in the first 60%.
          const anim = perks.animate([
            {height: px(state.h), paddingTop: state.pt, paddingBottom: state.pb, opacity: state.o, offset: 0},
            {opacity: 0, offset: .6},
            {height: '0px', paddingTop: '0px', paddingBottom: '0px', opacity: 0, offset: 1}
          ], {duration: OFFER_MS, easing: EASE, fill: 'forwards'});
          perkAnims.set(perks, anim);
          settle(anim, () => {
            if (perkAnims.get(perks) !== anim) return;
            card.classList.remove('apm-closing'); perks.classList.remove('apm-tween'); perkAnims.delete(perks);
            anim.cancel();
          });
        }
      });
    });
  });

  /* 2. Bag drawer -------------------------------------------------------------------------------------------- */
  function setupBag(bag) {
    if (!bag || bag.dataset.apmBag) return;
    bag.dataset.apmBag = 'true';
    const nativeClose = HTMLDialogElement.prototype.close;
    const nativeShowModal = HTMLDialogElement.prototype.showModal;
    let leaving = null;
    const offset = () => (bag.getBoundingClientRect().left > 8 ? 'translateX(28px)' : 'translateY(18px)');
    const enter = () => {
      if (still()) return;
      bag.animate([{opacity: 0, transform: offset()}, {opacity: 1, transform: 'none'}], {duration: 280, easing: EASE});
      try { bag.animate([{opacity: 0}, {opacity: 1}], {duration: 240, easing: 'linear', pseudoElement: '::backdrop'}); } catch (_) {}
    };
    const stopLeaving = () => { if (!leaving) return; const l = leaving; leaving = null; l.forEach(a => a.cancel()); };

    bag.showModal = function () {
      if (leaving) { stopLeaving(); enter(); return; } // reopened during the exit: stay open
      if (this.open) return;
      nativeShowModal.call(this);
      enter();
    };
    bag.close = function (value) {
      if (!this.open) return;
      if (leaving) return; // exit already running
      if (still()) { nativeClose.call(this, value); return; }
      const anims = [bag.animate([{opacity: 1, transform: 'none'}, {opacity: 0, transform: offset()}], {duration: 200, easing: EASE_OUT, fill: 'forwards'})];
      try { anims.push(bag.animate([{opacity: 1}, {opacity: 0}], {duration: 200, easing: 'linear', pseudoElement: '::backdrop', fill: 'forwards'})); } catch (_) {}
      leaving = anims;
      Promise.all(anims.map(a => a.finished)).then(() => {
        if (leaving !== anims) return;
        leaving = null;
        nativeClose.call(bag, value);
        anims.forEach(a => a.cancel());
      }, () => {});
    };
    // Escape: animate the exit instead of the instant native close.
    bag.addEventListener('cancel', e => { if (still() || leaving) return; e.preventDefault(); bag.close(); });
    // If a third party opens it with the native method, still run the entrance once.
    new MutationObserver(() => { if (bag.open && !leaving && !bag.getAnimations().length) enter(); })
      .observe(bag, {attributes: true, attributeFilter: ['open']});
  }

  /* 3. Accordions -------------------------------------------------------------------------------------------- */
  const detailAnims = new WeakMap();
  document.addEventListener('click', e => {
    const summary = e.target.closest && e.target.closest('summary');
    if (!summary) return;
    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement) || !details.closest('main') || details.querySelector(':scope > summary') !== summary) return;
    if (!document.body.classList.contains('pdp-page')) return; // theme port: the homepage keeps native disclosures
    if (still()) return; // native toggle
    e.preventDefault();
    const start = details.getBoundingClientRect().height;
    const running = detailAnims.get(details);
    const closing = running ? running.apmOpening : details.open; // reverse a running animation
    running?.cancel();
    details.classList.add('apm-tween');
    let end;
    if (closing) {
      details.open = false; end = details.getBoundingClientRect().height; details.open = true;
    } else {
      details.open = true; end = details.getBoundingClientRect().height;
    }
    const anim = details.animate([{height: px(start)}, {height: px(end)}], {duration: closing ? 240 : 300, easing: closing ? EASE_OUT : EASE});
    anim.apmOpening = !closing;
    detailAnims.set(details, anim);
    if (!closing) {
      [...details.children].filter(c => c !== summary).forEach(c => c.animate([{opacity: 0, transform: 'translateY(-4px)'}, {opacity: 1, transform: 'none'}], {duration: 260, delay: 40, easing: EASE, fill: 'backwards'}));
    }
    const done = () => {
      if (detailAnims.get(details) !== anim) return;
      detailAnims.delete(details);
      details.classList.remove('apm-tween');
      if (closing) details.open = false;
    };
    anim.addEventListener('finish', done, {once: true});
  });

  /* 4. Renewal note ------------------------------------------------------------------------------------------ */
  function setupRenewal(note) {
    if (!note || note.dataset.apmNote) return;
    note.dataset.apmNote = 'true';
    let last = note.textContent;
    new MutationObserver(() => {
      if (note.textContent === last) return;
      last = note.textContent;
      if (still()) return;
      note.getAnimations().forEach(a => a.cancel());
      note.animate([{opacity: .35}, {opacity: 1}], {duration: 260, easing: EASE});
    }).observe(note, {childList: true, characterData: true, subtree: true});
  }

  function setup() {
    setupBag(document.getElementById('bag-dialog'));
    setupRenewal(document.getElementById('renewal-note'));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setup, {once: true}); else setup();
  window.apPdpMotion = {version: 'v5', setup};
})();
